import React, { useState } from 'react';
import styled from 'styled-components';
import { Send, Clock, MessageSquare, User, Calendar, FileText, AlertCircle, CheckCircle, RefreshCw, Phone } from 'lucide-react';
import ContentLayout from '../../../Common/Component/ContentLayout';

const Container = styled.div`
  width: 100%;
`;

const ProgressCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  overflow: hidden;
  margin-bottom: 20px;
`;

const CardHeader = styled.div`
  background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%);
  color: white;
  padding: 25px;
`;

const QuoteTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 10px 0;
`;

const QuoteId = styled.p`
  opacity: 0.9;
  margin: 0 0 5px 0;
  font-size: 14px;
`;

const QuoteDate = styled.p`
  opacity: 0.8;
  margin: 0;
  font-size: 12px;
`;

const ProgressSection = styled.div`
  padding: 25px;
`;

const ProgressTimeline = styled.div`
  position: relative;
  padding-left: 30px;
  
  &::before {
    content: '';
    position: absolute;
    left: 15px;
    top: 0;
    bottom: 0;
    width: 2px;
    background: #e5e7eb;
  }
`;

const TimelineItem = styled.div`
  position: relative;
  padding-bottom: 30px;
  
  &:last-child {
    padding-bottom: 0;
  }
`;

const TimelineIcon = styled.div`
  position: absolute;
  left: -23px;
  top: 0;
  width: 30px;
  height: 30px;
  background: ${props => props.active ? '#3b82f6' : props.completed ? '#10b981' : '#e5e7eb'};
  border: 3px solid white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => (props.active || props.completed) ? 'white' : '#9ca3af'};
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
`;

const TimelineContent = styled.div`
  margin-left: 15px;
`;

const TimelineTime = styled.p`
  font-size: 12px;
  color: #6b7280;
  margin: 0 0 5px 0;
`;

const TimelineTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 8px 0;
`;

const TimelineDescription = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
`;

const CurrentStatusBox = styled.div`
  background: #fef3c7;
  border: 1px solid #fbbf24;
  border-radius: 8px;
  padding: 20px;
  margin: 20px 0;
`;

const StatusTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #d97706;
  margin: 0 0 10px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatusDescription = styled.p`
  color: #92400e;
  margin: 0 0 15px 0;
  line-height: 1.5;
`;

const ContactInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  background: #f9fafb;
  padding: 15px;
  border-radius: 6px;
  margin-top: 15px;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #374151;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 20px;
`;

const ActionButton = styled.button`
  flex: 1;
  padding: 12px 20px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: ${props => props.primary ? '#3b82f6' : '#6b7280'};
  color: white;
  
  &:hover {
    background: ${props => props.primary ? '#2563eb' : '#4b5563'};
  }
`;

const ProjectDetails = styled.div`
  background: #f9fafb;
  border-radius: 8px;
  padding: 20px;
  margin: 20px 0;
`;

const DetailRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 15px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailItem = styled.div``;

const DetailLabel = styled.p`
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  margin: 0 0 5px 0;
`;

const DetailValue = styled.p`
  font-size: 14px;
  color: #1f2937;
  margin: 0;
  font-weight: 500;
`;

const MessageThread = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  max-height: 300px;
  overflow-y: auto;
`;

const MessageItem = styled.div`
  padding: 15px;
  border-bottom: 1px solid #f3f4f6;
  
  &:last-child {
    border-bottom: none;
  }
`;

const MessageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const MessageAuthor = styled.span`
  font-weight: 600;
  color: ${props => props.isClient ? '#3b82f6' : '#10b981'};
  font-size: 14px;
`;

const MessageTime = styled.span`
  font-size: 12px;
  color: #6b7280;
`;

const MessageContent = styled.p`
  font-size: 14px;
  color: #374151;
  margin: 0;
  line-height: 1.5;
`;

const NewMessageForm = styled.div`
  margin-top: 15px;
`;

const MessageInput = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

function InProgressQuotes() {
  const [newMessage, setNewMessage] = useState('');

  // 더미 진행중 견적 데이터
  const inProgressQuote = {
    id: 'QT-2024-001',
    title: '홈페이지 리뉴얼 견적 요청',
    category: '웹개발',
    requestDate: '2024-02-15',
    budget: '300-500만원',
    deadline: '2024-03-15',
    description: '기존 홈페이지를 반응형으로 리뉴얼하고 관리자 페이지도 포함',
    client: {
      name: '김철수',
      email: 'kimcs@example.com',
      phone: '010-1234-5678',
      company: '(주)테크놀로지'
    },
    assignedTo: {
      name: '박프로젝트매니저',
      email: 'pm.park@antsmarket.com',
      phone: '02-1234-5678'
    },
    currentStatus: 'requirement-analysis',
    progress: 35,
    timeline: [
      {
        step: 'submitted',
        title: '견적 요청 접수',
        description: '견적 요청이 성공적으로 접수되었습니다.',
        time: '2024-02-15 14:30',
        completed: true,
        active: false
      },
      {
        step: 'assigned',
        title: '담당자 배정',
        description: '프로젝트 매니저가 배정되어 검토를 시작했습니다.',
        time: '2024-02-16 09:00',
        completed: true,
        active: false
      },
      {
        step: 'analysis',
        title: '요구사항 분석',
        description: '상세한 요구사항을 분석하고 있습니다.',
        time: '2024-02-16 10:00',
        completed: false,
        active: true
      },
      {
        step: 'estimation',
        title: '견적 산정',
        description: '분석 결과를 바탕으로 정확한 견적을 산정합니다.',
        time: '예정',
        completed: false,
        active: false
      },
      {
        step: 'review',
        title: '견적서 검토',
        description: '내부 검토를 통해 최종 견적서를 완성합니다.',
        time: '예정',
        completed: false,
        active: false
      },
      {
        step: 'delivery',
        title: '견적서 전달',
        description: '완성된 견적서를 고객에게 전달합니다.',
        time: '예정',
        completed: false,
        active: false
      }
    ],
    messages: [
      {
        id: 1,
        author: '박프로젝트매니저',
        isClient: false,
        time: '2024-02-16 10:30',
        content: '안녕하세요! 홈페이지 리뉴얼 견적 요청을 검토하고 있습니다. 현재 사이트의 URL과 현재 사용 중인 기술 스택에 대해 알려주시면 더 정확한 견적을 제공할 수 있습니다.'
      },
      {
        id: 2,
        author: '김철수',
        isClient: true,
        time: '2024-02-16 14:20',
        content: '현재 사이트는 www.techcorp.co.kr이고, WordPress로 구축되어 있습니다. 관리가 어려워서 React 기반으로 새로 만들고 싶습니다.'
      },
      {
        id: 3,
        author: '박프로젝트매니저',
        isClient: false,
        time: '2024-02-16 16:45',
        content: '확인했습니다. React + Node.js 기반으로 구축하는 방향으로 검토하겠습니다. 관리자 페이지의 구체적인 기능 요구사항도 정리해서 보내주시면 감사하겠습니다.'
      }
    ]
  };

  const getTimelineIcon = (step, completed, active) => {
    if (completed) return <CheckCircle size={14} />;
    if (active) return <RefreshCw size={14} />;
    return <Clock size={14} />;
  };

  const handleSendMessage = () => {
    if (newMessage.trim()) {
      // 실제로는 서버에 메시지를 전송
      console.log('Sending message:', newMessage);
      setNewMessage('');
    }
  };

  return (
    <ContentLayout 
      title="진행중인 견적"
      subtitle="현재 진행중인 견적의 상세 진행 상황을 확인하고 소통할 수 있습니다"
    >
      <Container>
        <ProgressCard>
          <CardHeader>
            <QuoteTitle>{inProgressQuote.title}</QuoteTitle>
            <QuoteId>견적번호: {inProgressQuote.id}</QuoteId>
            <QuoteDate>요청일: {inProgressQuote.requestDate}</QuoteDate>
          </CardHeader>

          <ProjectDetails>
            <DetailRow>
              <DetailItem>
                <DetailLabel>카테고리</DetailLabel>
                <DetailValue>{inProgressQuote.category}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>예산 범위</DetailLabel>
                <DetailValue>{inProgressQuote.budget}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>희망 완료일</DetailLabel>
                <DetailValue>{inProgressQuote.deadline}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>진행률</DetailLabel>
                <DetailValue>{inProgressQuote.progress}%</DetailValue>
              </DetailItem>
            </DetailRow>
            <DetailRow>
              <DetailItem>
                <DetailLabel>담당자</DetailLabel>
                <DetailValue>{inProgressQuote.assignedTo.name}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>담당자 연락처</DetailLabel>
                <DetailValue>{inProgressQuote.assignedTo.email}</DetailValue>
              </DetailItem>
            </DetailRow>
          </ProjectDetails>

          <CurrentStatusBox>
            <StatusTitle>
              <RefreshCw size={16} />
              현재 진행 상황
            </StatusTitle>
            <StatusDescription>
              요구사항 분석 단계입니다. 담당자가 프로젝트의 세부 사항을 검토하고 있으며, 
              추가 질문이나 자료 요청이 있을 수 있습니다.
            </StatusDescription>
            <ContactInfo>
              <ContactItem>
                <User size={14} />
                담당자: {inProgressQuote.assignedTo.name}
              </ContactItem>
              <ContactItem>
                <Phone size={14} />
                연락처: {inProgressQuote.assignedTo.phone}
              </ContactItem>
            </ContactInfo>
          </CurrentStatusBox>

          <ProgressSection>
            <h3 style={{marginBottom: '20px', color: '#1f2937'}}>진행 단계</h3>
            <ProgressTimeline>
              {inProgressQuote.timeline.map((item, index) => (
                <TimelineItem key={index}>
                  <TimelineIcon 
                    completed={item.completed} 
                    active={item.active}
                  >
                    {getTimelineIcon(item.step, item.completed, item.active)}
                  </TimelineIcon>
                  <TimelineContent>
                    <TimelineTime>{item.time}</TimelineTime>
                    <TimelineTitle>{item.title}</TimelineTitle>
                    <TimelineDescription>{item.description}</TimelineDescription>
                  </TimelineContent>
                </TimelineItem>
              ))}
            </ProgressTimeline>
          </ProgressSection>

          <ProgressSection>
            <h3 style={{marginBottom: '15px', color: '#1f2937'}}>
              소통 내역 ({inProgressQuote.messages.length})
            </h3>
            <MessageThread>
              {inProgressQuote.messages.map(message => (
                <MessageItem key={message.id}>
                  <MessageHeader>
                    <MessageAuthor isClient={message.isClient}>
                      {message.author}
                      {message.isClient ? ' (고객)' : ' (담당자)'}
                    </MessageAuthor>
                    <MessageTime>{message.time}</MessageTime>
                  </MessageHeader>
                  <MessageContent>{message.content}</MessageContent>
                </MessageItem>
              ))}
            </MessageThread>

            <NewMessageForm>
              <MessageInput
                placeholder="담당자에게 메시지를 남겨보세요..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
              />
              <ActionButtons>
                <ActionButton primary onClick={handleSendMessage}>
                  <Send size={14} />
                  메시지 전송
                </ActionButton>
                <ActionButton>
                  <Phone size={14} />
                  전화 상담 예약
                </ActionButton>
              </ActionButtons>
            </NewMessageForm>
          </ProgressSection>
        </ProgressCard>
      </Container>
    </ContentLayout>
  );
}

export default InProgressQuotes;